import { useState, useEffect, useRef } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Database, Download, Upload, AlertTriangle, CheckCircle2, Loader2, FileJson, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function AdminDataTools() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [wipe, setWipe] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-tools/stats`);
      setStats(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_data_tools:stats_failed", { defaultValue: "Failed to load stats" }));
    } finally {
      setStatsLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/data-tools/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `tablecy-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("admin_data_tools:export_done", { defaultValue: "Export downloaded" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_data_tools:export_failed", { defaultValue: "Export failed" }));
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    let data;
    try {
      const text = await file.text();
      data = JSON.parse(text);
    } catch {
      toast.error(t("admin_data_tools:bad_json", { defaultValue: "Invalid JSON file" }));
      return;
    }
    if (!data?.collections) {
      toast.error(t("admin_data_tools:bad_format", { defaultValue: "File doesn't look like a TableCy export" }));
      return;
    }
    const collectionCount = Object.keys(data.collections).length;
    const totalDocs = Object.values(data.collections).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
    const verb = wipe
      ? t("admin_data_tools:confirm_wipe", {
          defaultValue: "WIPE every target collection and replace with {{docs}} docs across {{cols}} collections? This cannot be undone.",
          docs: totalDocs,
          cols: collectionCount,
        })
      : t("admin_data_tools:confirm_merge", {
          defaultValue: "Merge/upsert {{docs}} docs across {{cols}} collections (existing docs with the same ID will be overwritten)?",
          docs: totalDocs,
          cols: collectionCount,
        });
    if (!window.confirm(verb)) return;

    setImporting(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/data-tools/import?wipe=${wipe ? "true" : "false"}`, data);
      setImportResult(res.data);
      toast.success(t("admin_data_tools:import_done", { defaultValue: "Import complete" }));
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_data_tools:import_failed", { defaultValue: "Import failed" }));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-data-tools-page" className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Database className="h-6 w-6 text-primary" />
          {t("admin_data_tools:title", { defaultValue: "Data Migration" })}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("admin_data_tools:subtitle", {
            defaultValue:
              "Export every venue, taxonomy, page, and config to a JSON file you can re-import after deploying to production.",
          })}
        </p>
      </div>

      {/* Export */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Download className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <h2 className="font-medium text-foreground">
              {t("admin_data_tools:export_title", { defaultValue: "Export Database" })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("admin_data_tools:export_help", {
                defaultValue:
                  "Downloads a single JSON dump of all collections. Save it before deploying — you'll need it to re-seed production.",
              })}
            </p>
          </div>
        </div>

        <Button onClick={handleExport} disabled={exporting} data-testid="export-btn">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {exporting
            ? t("admin_data_tools:exporting", { defaultValue: "Exporting..." })
            : t("admin_data_tools:export_btn", { defaultValue: "Export All Data" })}
        </Button>

        {/* Stats grid */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
          {statsLoading ? (
            <div className="col-span-full animate-pulse h-20 bg-muted rounded" />
          ) : (
            stats?.collections.map((c) => (
              <div
                key={c.collection}
                className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2"
                data-testid={`stat-${c.collection}`}
              >
                <span className="font-mono text-muted-foreground truncate">{c.collection}</span>
                <span className="font-medium text-foreground">{c.count}</span>
              </div>
            ))
          )}
        </div>
        {stats && (
          <p className="text-xs text-muted-foreground mt-3">
            {t("admin_data_tools:total_docs", { defaultValue: "Total documents" })}: <strong>{stats.total}</strong>
          </p>
        )}
      </div>

      {/* Import */}
      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Upload className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <h2 className="font-medium text-foreground">
              {t("admin_data_tools:import_title", { defaultValue: "Import Database" })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("admin_data_tools:import_help", {
                defaultValue:
                  "Upload a previously exported JSON file. By default, existing docs are merged/upserted by their unique ID.",
              })}
            </p>
          </div>
        </div>

        <label className="flex items-center gap-3 p-3 border border-amber-300 bg-amber-50 rounded-lg mb-4 cursor-pointer hover:bg-amber-100 transition-colors">
          <input
            type="checkbox"
            checked={wipe}
            onChange={(e) => setWipe(e.target.checked)}
            className="rounded"
            data-testid="wipe-checkbox"
          />
          <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
          <span className="text-sm text-amber-900">
            {t("admin_data_tools:wipe_option", {
              defaultValue: "Wipe each target collection before importing (DESTRUCTIVE — replaces data instead of merging)",
            })}
          </span>
        </label>

        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={(e) => handleImport(e.target.files?.[0])}
          className="hidden"
          data-testid="import-file-input"
        />
        <Button
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          data-testid="import-btn"
        >
          {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileJson className="h-4 w-4 mr-2" />}
          {importing
            ? t("admin_data_tools:importing", { defaultValue: "Importing..." })
            : t("admin_data_tools:import_btn", { defaultValue: "Choose JSON File" })}
        </Button>

        {importResult && (
          <div
            className="mt-5 bg-green-50 border border-green-200 rounded-lg p-4"
            data-testid="import-result"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <h3 className="font-medium text-green-900">
                {t("admin_data_tools:result_title", { defaultValue: "Import complete" })}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
              <div>
                <p className="text-xs text-green-700">{t("admin_data_tools:inserted", { defaultValue: "Inserted" })}</p>
                <p className="font-semibold text-green-900">{importResult.inserted}</p>
              </div>
              <div>
                <p className="text-xs text-green-700">{t("admin_data_tools:updated", { defaultValue: "Updated" })}</p>
                <p className="font-semibold text-green-900">{importResult.updated}</p>
              </div>
              <div>
                <p className="text-xs text-green-700">{t("admin_data_tools:wiped", { defaultValue: "Wiped" })}</p>
                <p className="font-semibold text-green-900">{importResult.wiped}</p>
              </div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-green-700 hover:text-green-900">
                {t("admin_data_tools:show_breakdown", { defaultValue: "Show per-collection breakdown" })}
              </summary>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono">
                {Object.entries(importResult.by_collection || {}).map(([col, cnt]) => (
                  <div key={col} className="text-green-800">
                    {col}: +{cnt.inserted} / ↻{cnt.updated}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Photos & uploads explainer */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 text-sm text-indigo-900 mb-6">
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          {t("admin_data_tools:photos_title", { defaultValue: "What about photos & uploaded files?" })}
        </h3>
        <p className="text-indigo-800 mb-2">
          {t("admin_data_tools:photos_explain", {
            defaultValue:
              "Uploaded images (venue covers, galleries, menu items, logos) are stored in Emergent's managed object storage — NOT on disk. The export above includes the file metadata (the `files` collection), and the actual binaries persist across deployments automatically because they live in shared cloud storage keyed by your app namespace.",
          })}
        </p>
        <p className="text-indigo-800">
          <strong>{t("admin_data_tools:photos_tldr_label", { defaultValue: "TL;DR:" })}</strong>{" "}
          {t("admin_data_tools:photos_tldr", {
            defaultValue:
              "Just export DB → deploy → import DB. Photos will still load on production with no extra steps.",
          })}
        </p>
      </div>

      {/* Help block */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-900">
        <h3 className="font-medium mb-2">
          {t("admin_data_tools:workflow_title", { defaultValue: "Recommended Deploy Workflow" })}
        </h3>
        <ol className="list-decimal pl-5 space-y-1 text-blue-800">
          <li>{t("admin_data_tools:step_1", { defaultValue: "Click \"Export All Data\" here in preview." })}</li>
          <li>{t("admin_data_tools:step_2", { defaultValue: "Deploy your app to production." })}</li>
          <li>{t("admin_data_tools:step_3", { defaultValue: "Log in to /admin on production with the default super_admin." })}</li>
          <li>{t("admin_data_tools:step_4", { defaultValue: "Open this page on production → upload the JSON dump." })}</li>
          <li>{t("admin_data_tools:step_5", { defaultValue: "Done — every venue, owner, page, and setting is restored." })}</li>
        </ol>
      </div>
    </div>
  );
}
